import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  TargetIcon,
  PlusIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FilterIcon,
  CalendarIcon,
  HashIcon,
  EditIcon,
  TrashIcon,
  FolderIcon,
  Building2Icon,
  MapPinIcon,
  GraduationCapIcon,
  MapIcon,
  GlobeIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getLearningLevelTranslation } from '../../services/learningLevelsTranslationsService';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../services/departmentsTranslationsService';

const EMPTY_SCOPE_FORM = {
  countryId: '',
  regionId: '',
  academyId: '',
  districtId: '',
  institutionId: '',
  departmentId: ''
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'align',
  'link', 'image', 'video',
  'blockquote', 'code-block'
];

function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function stripHtmlPreview(html, maxLen = 100) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
}

function formatDateDisplay(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function hasAnyScopeId(scope) {
  if (!scope || typeof scope !== 'object') return false;
  return [scope.countryId, scope.regionId, scope.academyId, scope.districtId, scope.institutionId, scope.departmentId].some(
    (v) => v != null && v !== ''
  );
}

const Challenges = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image', 'video'],
          ['blockquote', 'code-block'],
          ['clean']
        ],
        handlers: {
          image: function imageUrlHandler() {
            const url = window.prompt(t('challenges.imageUrlPrompt'));
            if (url == null) return;
            const trimmed = url.trim();
            if (!trimmed) return;
            const range = this.quill.getSelection(true);
            const index = range ? range.index : Math.max(0, this.quill.getLength() - 1);
            this.quill.insertEmbed(index, 'image', trimmed, 'user');
            this.quill.setSelection(index + 1, 0, 'silent');
          }
        }
      }
    }),
    [t]
  );

  const [learningLevels, setLearningLevels] = useState([]);
  const [loadingLearningLevels, setLoadingLearningLevels] = useState(false);

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(false);

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const defaultForm = useMemo(
    () => ({
      name: '',
      description: '',
      correctAnswer: '',
      startsAt: '',
      endsAt: '',
      status: 'draft',
      scope: { ...EMPTY_SCOPE_FORM },
      learningLevelId: ''
    }),
    []
  );

  const [form, setForm] = useState(defaultForm);

  const [orgLookupMaps, setOrgLookupMaps] = useState({
    countries: {},
    regions: {},
    academies: {},
    districts: {},
    institutions: {},
    departments: {}
  });

  const loadOrganizationalLookupMaps = useCallback(async () => {
    try {
      const [countriesRes, regionsRes, academiesRes, districtsRes, institutionsRes, departmentsRes] = await Promise.all([
        api.get('/organization/countries?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/regions?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/academies?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/districts?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/institutions?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/departments?page=1&limit=100').catch(() => ({ data: { success: false } }))
      ]);

      const maps = {
        countries: {},
        regions: {},
        academies: {},
        districts: {},
        institutions: {},
        departments: {}
      };

      if (countriesRes.data?.success) {
        countriesRes.data.data?.countries?.forEach((item) => {
          maps.countries[item.id] = {
            name: item.name,
            translatedName: getCountryTranslation(item.name, currentLanguage)
          };
        });
      }
      if (regionsRes.data?.success) {
        regionsRes.data.data?.regions?.forEach((item) => {
          maps.regions[item.id] = {
            name: item.name,
            translatedName: getRegionTranslation(item.name, currentLanguage)
          };
        });
      }
      if (academiesRes.data?.success) {
        academiesRes.data.data?.academies?.forEach((item) => {
          maps.academies[item.id] = {
            name: item.name,
            translatedName: getAcademyTranslation(item.name, currentLanguage)
          };
        });
      }
      if (districtsRes.data?.success) {
        districtsRes.data.data?.districts?.forEach((item) => {
          maps.districts[item.id] = {
            name: item.name,
            translatedName: getDistrictTranslation(item.name, currentLanguage)
          };
        });
      }
      if (institutionsRes.data?.success) {
        institutionsRes.data.data?.institutions?.forEach((item) => {
          maps.institutions[item.id] = {
            name: item.name,
            translatedName: getInstitutionTranslation(item.name, currentLanguage)
          };
        });
      }
      if (departmentsRes.data?.success) {
        departmentsRes.data.data?.departments?.forEach((item) => {
          maps.departments[item.id] = {
            name: item.name,
            translatedName: getDepartmentTranslation(item.name, currentLanguage)
          };
        });
      }

      setOrgLookupMaps(maps);
    } catch (error) {
      console.error('loadOrganizationalLookupMaps', error);
    }
  }, [currentLanguage]);

  useEffect(() => {
    loadOrganizationalLookupMaps();
  }, [loadOrganizationalLookupMaps]);

  const fetchChallenges = async (page = 1, status = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (status && status !== 'all') {
        params.set('status', status);
      }
      const response = await api.get(`/challenges?${params.toString()}`);
      if (response.data?.success) {
        const data = response.data.data || {};
        setChallenges(data.challenges || []);
        const p = data.pagination || {};
        setTotal(p.total || 0);
        setCurrentPage(p.page || page);
        setTotalPages(p.pages || Math.ceil((p.total || 0) / (p.limit || limit)) || 1);
      }
    } catch (error) {
      console.error('fetch challenges', error);
      toast.error(error.response?.data?.message || t('challenges.fetchError'), {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges(1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const loadLearningLevels = async () => {
      setLoadingLearningLevels(true);
      try {
        const res = await api.get('/organization/learning-levels?page=1&limit=100');
        if (res.data?.success) {
          const list = res.data.data?.learningLevels || [];
          setLearningLevels(
            [...list].sort((a, b) => (a.level_number ?? 0) - (b.level_number ?? 0))
          );
        }
      } catch (e) {
        console.error('learning levels', e);
        toast.error(t('challenges.levelsLoadError'), { duration: 3000, position: 'top-center' });
      } finally {
        setLoadingLearningLevels(false);
      }
    };
    loadLearningLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  useEffect(() => {
    const loadCountries = async () => {
      setLoadingOrgData(true);
      try {
        const countriesResponse = await api.get('/organization/countries?page=1&limit=100');
        if (countriesResponse.data?.success) {
          const list = countriesResponse.data.data?.countries || [];
          setCountries(
            list.map((country) => ({
              ...country,
              translatedName: getCountryTranslation(country.name, currentLanguage)
            }))
          );
        }
      } catch (e) {
        console.error('countries', e);
        toast.error(t('users.orgDataFetchError'), { duration: 3000, position: 'top-center' });
      } finally {
        setLoadingOrgData(false);
      }
    };
    loadCountries();
  }, [currentLanguage, t]);

  const fetchRegionsByCountry = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    try {
      const response = await api.get(`/organization/countries/${countryId}/regions?page=1&limit=100`);
      if (response.data?.success) {
        const list = response.data.data?.regions || [];
        setRegions(
          list.map((region) => ({
            ...region,
            translatedName: getRegionTranslation(region.name, currentLanguage)
          }))
        );
      }
    } catch (e) {
      console.error('regions', e);
    }
  };

  const fetchAcademiesByRegion = async (regionId) => {
    if (!regionId) {
      setAcademies([]);
      return;
    }
    try {
      const response = await api.get(`/organization/regions/${regionId}/academies?page=1&limit=100`);
      if (response.data?.success) {
        const list = response.data.data?.academies || [];
        setAcademies(
          list.map((academy) => ({
            ...academy,
            translatedName: getAcademyTranslation(academy.name, currentLanguage)
          }))
        );
      }
    } catch (e) {
      console.error('academies', e);
    }
  };

  const fetchDistrictsByAcademy = async (academyId) => {
    if (!academyId) {
      setDistricts([]);
      return;
    }
    try {
      const response = await api.get(`/organization/academies/${academyId}/districts?page=1&limit=100`);
      if (response.data?.success) {
        const list = response.data.data?.districts || [];
        setDistricts(
          list.map((district) => ({
            ...district,
            translatedName: getDistrictTranslation(district.name, currentLanguage)
          }))
        );
      }
    } catch (e) {
      console.error('districts', e);
    }
  };

  const fetchInstitutionsByDistrict = async (districtId) => {
    if (!districtId) {
      setInstitutions([]);
      return;
    }
    try {
      const response = await api.get(`/organization/districts/${districtId}/institutions?page=1&limit=100`);
      if (response.data?.success) {
        const list = response.data.data?.institutions || [];
        setInstitutions(
          list.map((institution) => ({
            ...institution,
            translatedName: getInstitutionTranslation(institution.name, currentLanguage)
          }))
        );
      }
    } catch (e) {
      console.error('institutions', e);
    }
  };

  const fetchDepartmentsByInstitution = async (institutionId) => {
    if (!institutionId) {
      setDepartments([]);
      return;
    }
    try {
      const response = await api.get(`/organization/institutions/${institutionId}/departments?page=1&limit=100`);
      if (response.data?.success) {
        const list = response.data.data?.departments || [];
        setDepartments(
          list.map((department) => ({
            ...department,
            translatedName: getDepartmentTranslation(department.name, currentLanguage)
          }))
        );
      }
    } catch (e) {
      console.error('departments', e);
    }
  };

  const handleScopeChange = (field, value) => {
    const v = value || '';
    if (field === 'countryId') {
      setForm((prev) => ({
        ...prev,
        scope: {
          countryId: v,
          regionId: '',
          academyId: '',
          districtId: '',
          institutionId: '',
          departmentId: ''
        }
      }));
      setRegions([]);
      setAcademies([]);
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (v) fetchRegionsByCountry(v);
    } else if (field === 'regionId') {
      setForm((prev) => ({
        ...prev,
        scope: {
          ...prev.scope,
          regionId: v,
          academyId: '',
          districtId: '',
          institutionId: '',
          departmentId: ''
        }
      }));
      setAcademies([]);
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (v) fetchAcademiesByRegion(v);
    } else if (field === 'academyId') {
      setForm((prev) => ({
        ...prev,
        scope: {
          ...prev.scope,
          academyId: v,
          districtId: '',
          institutionId: '',
          departmentId: ''
        }
      }));
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (v) fetchDistrictsByAcademy(v);
    } else if (field === 'districtId') {
      setForm((prev) => ({
        ...prev,
        scope: {
          ...prev.scope,
          districtId: v,
          institutionId: '',
          departmentId: ''
        }
      }));
      setInstitutions([]);
      setDepartments([]);
      if (v) fetchInstitutionsByDistrict(v);
    } else if (field === 'institutionId') {
      setForm((prev) => ({
        ...prev,
        scope: {
          ...prev.scope,
          institutionId: v,
          departmentId: ''
        }
      }));
      setDepartments([]);
      if (v) fetchDepartmentsByInstitution(v);
    } else if (field === 'departmentId') {
      setForm((prev) => ({
        ...prev,
        scope: { ...prev.scope, departmentId: v }
      }));
    }
  };

  const loadScopeCascadeForEdit = async (scope) => {
    if (!scope) return;
    const cid = scope.countryId;
    const rid = scope.regionId;
    const aid = scope.academyId;
    const did = scope.districtId;
    const iid = scope.institutionId;
    if (cid) {
      await fetchRegionsByCountry(cid);
      if (rid) {
        await fetchAcademiesByRegion(rid);
        if (aid) {
          await fetchDistrictsByAcademy(aid);
          if (did) {
            await fetchInstitutionsByDistrict(did);
            if (iid) {
              await fetchDepartmentsByInstitution(iid);
            }
          }
        }
      }
    }
  };

  const getLevelDisplayName = (level) => {
    if (!level) return '';
    const translation = getLearningLevelTranslation(level.name);
    const lang = currentLanguage || 'fr';
    if (translation) return translation[lang] || level.name;
    return level.name;
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const scopeFromApiToForm = (s) => ({
    countryId: s?.countryId != null && s?.countryId !== '' ? String(s.countryId) : '',
    regionId: s?.regionId != null && s?.regionId !== '' ? String(s.regionId) : '',
    academyId: s?.academyId != null && s?.academyId !== '' ? String(s.academyId) : '',
    districtId: s?.districtId != null && s?.districtId !== '' ? String(s.districtId) : '',
    institutionId: s?.institutionId != null && s?.institutionId !== '' ? String(s.institutionId) : '',
    departmentId: s?.departmentId != null && s?.departmentId !== '' ? String(s.departmentId) : ''
  });

  const challengeToFormState = (c) => ({
    name: c.name || '',
    description: c.description && c.description.trim() ? c.description : '<p><br></p>',
    correctAnswer: c.correctAnswer ?? '',
    startsAt: isoToDatetimeLocal(c.startsAt),
    endsAt: isoToDatetimeLocal(c.endsAt),
    status: c.status || 'draft',
    scope: scopeFromApiToForm(c.scope),
    learningLevelId: c.learningLevelId != null && c.learningLevelId !== '' ? String(c.learningLevelId) : ''
  });

  const openAddModal = () => {
    setEditingId(null);
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    setForm({
      ...defaultForm,
      description: '<p><br></p>',
      startsAt: isoToDatetimeLocal(new Date().toISOString()),
      endsAt: isoToDatetimeLocal(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
    });
    setShowModal(true);
  };

  const openEditModal = async (challenge) => {
    setEditingId(challenge.id);
    setForm(challengeToFormState(challenge));
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    setShowModal(true);
    await loadScopeCascadeForEdit(challenge.scope);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = () => {
    const learningLevelId =
      form.learningLevelId === '' || form.learningLevelId == null
        ? null
        : Number(form.learningLevelId);

    return {
      name: form.name.trim(),
      description: form.description || '',
      answerType: 'text',
      correctAnswer: form.correctAnswer.trim(),
      startsAt: datetimeLocalToIso(form.startsAt),
      endsAt: datetimeLocalToIso(form.endsAt),
      status: form.status,
      scope: {
        countryId: form.scope.countryId ? Number(form.scope.countryId) : null,
        regionId: form.scope.regionId ? Number(form.scope.regionId) : null,
        academyId: form.scope.academyId ? Number(form.scope.academyId) : null,
        districtId: form.scope.districtId ? Number(form.scope.districtId) : null,
        institutionId: form.scope.institutionId ? Number(form.scope.institutionId) : null,
        departmentId: form.scope.departmentId ? Number(form.scope.departmentId) : null
      },
      learningLevelId
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload.name) {
      toast.error(t('challenges.nameRequired'), { position: 'top-center' });
      return;
    }
    if (!payload.startsAt || !payload.endsAt) {
      toast.error(t('challenges.datesRequired'), { position: 'top-center' });
      return;
    }
    if (!payload.correctAnswer) {
      toast.error(t('challenges.correctAnswerRequired'), { position: 'top-center' });
      return;
    }
    const descText = stripHtmlPreview(payload.description, 100000).trim();
    if (!descText) {
      toast.error(t('challenges.descriptionRequired'), { position: 'top-center' });
      return;
    }

    setIsSaving(true);
    try {
      if (editingId != null) {
        const response = await api.patch(`/challenges/${editingId}`, payload);
        if (response.data?.success) {
          toast.success(response.data.message || t('challenges.updateSuccess'), {
            duration: 3000,
            position: 'top-center'
          });
          closeModal();
          fetchChallenges(currentPage, statusFilter);
        }
      } else {
        const response = await api.post('/challenges', payload);
        if (response.data?.success) {
          toast.success(response.data.message || t('challenges.createSuccess'), {
            duration: 3000,
            position: 'top-center'
          });
          closeModal();
          setCurrentPage(1);
          fetchChallenges(1, statusFilter);
        }
      }
    } catch (error) {
      console.error('save challenge', error);
      const fallback =
        editingId != null ? t('challenges.updateError') : t('challenges.createError');
      toast.error(error.response?.data?.message || fallback, {
        duration: 4000,
        position: 'top-center'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (challenge) => {
    if (!window.confirm(t('challenges.deleteConfirm', { name: challenge.name }))) return;
    try {
      const response = await api.delete(`/challenges/${challenge.id}`);
      if (response.data?.success) {
        toast.success(response.data.message || t('challenges.deleteSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        fetchChallenges(currentPage, statusFilter);
      }
    } catch (error) {
      console.error('delete challenge', error);
      toast.error(error.response?.data?.message || t('challenges.deleteError'), {
        duration: 4000,
        position: 'top-center'
      });
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchChallenges(page, statusFilter);
  };

  const getPageNumbers = () => {
    const pages = [];
    const max = 5;
    if (totalPages <= max) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-amber-100 text-amber-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const challengesWithScope = useMemo(() => {
    return challenges.map((comp) => {
      const scopeInfo = { deepestLevel: null, deepestLevelIcon: null };
      const s = comp.scope;
      const m = orgLookupMaps;
      if (s?.departmentId && m.departments[s.departmentId]) {
        scopeInfo.deepestLevel = m.departments[s.departmentId].translatedName;
        scopeInfo.deepestLevelIcon = FolderIcon;
      } else if (s?.institutionId && m.institutions[s.institutionId]) {
        scopeInfo.deepestLevel = m.institutions[s.institutionId].translatedName;
        scopeInfo.deepestLevelIcon = Building2Icon;
      } else if (s?.districtId && m.districts[s.districtId]) {
        scopeInfo.deepestLevel = m.districts[s.districtId].translatedName;
        scopeInfo.deepestLevelIcon = MapPinIcon;
      } else if (s?.academyId && m.academies[s.academyId]) {
        scopeInfo.deepestLevel = m.academies[s.academyId].translatedName;
        scopeInfo.deepestLevelIcon = GraduationCapIcon;
      } else if (s?.regionId && m.regions[s.regionId]) {
        scopeInfo.deepestLevel = m.regions[s.regionId].translatedName;
        scopeInfo.deepestLevelIcon = MapIcon;
      } else if (s?.countryId && m.countries[s.countryId]) {
        scopeInfo.deepestLevel = m.countries[s.countryId].translatedName;
        scopeInfo.deepestLevelIcon = GlobeIcon;
      }
      return { ...comp, scopeInfo };
    });
  }, [challenges, orgLookupMaps]);

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100">
            <TargetIcon className="h-7 w-7 text-primary-600" aria-hidden />
          </div>
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h1 className="text-2xl font-bold text-gray-900">{t('challenges.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('challenges.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className={`inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <PlusIcon className="w-5 h-5" />
          {t('challenges.addChallenge')}
        </button>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <FilterIcon className="h-5 w-5 text-gray-400" />
        <label htmlFor="challenge-status-filter" className="text-sm font-medium text-gray-700">
          {t('challenges.filterByStatus')}
        </label>
        <select
          id="challenge-status-filter"
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">{t('challenges.statusAll')}</option>
          <option value="draft">{t('challenges.statusDraft')}</option>
          <option value="active">{t('challenges.statusActive')}</option>
          <option value="inactive">{t('challenges.statusInactive')}</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-12 text-center">
            <LoadingSpinner />
          </div>
        ) : challenges.length === 0 ? (
          <div className="p-12 text-center">
            <TargetIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('challenges.noChallenges')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('challenges.noChallengesHint')}</p>
            <button
              type="button"
              onClick={openAddModal}
              className={`mt-6 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <PlusIcon className="w-5 h-5" />
              {t('challenges.addFirst')}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colName')}
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colStructure')}
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colStarts')}
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colEnds')}
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colStatus')}
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        <HashIcon className="h-3.5 w-3.5" />
                        {t('challenges.colId')}
                      </span>
                    </th>
                    <th
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('challenges.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {challengesWithScope.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{row.name}</div>
                      </td>
                      <td className={`max-w-xs px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm text-gray-900">
                          {row.scopeInfo?.deepestLevel ? (
                            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                              {row.scopeInfo.deepestLevelIcon &&
                                React.createElement(row.scopeInfo.deepestLevelIcon, {
                                  className: `h-4 w-4 flex-shrink-0 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`
                                })}
                              <span className="break-words">{row.scopeInfo.deepestLevel}</span>
                            </div>
                          ) : hasAnyScopeId(row.scope) ? (
                            <span className="text-gray-500">{t('common.notAvailable')}</span>
                          ) : (
                            <span className="text-gray-500">{t('challenges.scopeGlobal')}</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                          {formatDateDisplay(row.startsAt)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {formatDateDisplay(row.endsAt)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
                        >
                          {t(`challenges.statusLabel.${row.status}`, { defaultValue: row.status })}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{row.id}</td>
                      <td className={`whitespace-nowrap px-6 py-4 ${isRTL ? 'text-left' : 'text-right'}`}>
                        <div className={`inline-flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center gap-1 rounded-md p-2 text-primary-600 hover:bg-primary-50"
                            title={t('challenges.editChallenge')}
                          >
                            <EditIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">{t('challenges.editChallenge')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="inline-flex items-center gap-1 rounded-md p-2 text-red-600 hover:bg-red-50"
                            title={t('challenges.deleteChallenge')}
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">{t('challenges.deleteChallenge')}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div
                className={`flex items-center justify-between border-t border-gray-200 px-4 py-3 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <p className="text-sm text-gray-600">
                  {t('challenges.showingPage', { page: currentPage, pages: totalPages, total })}
                </p>
                <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  {getPageNumbers().map((p, idx) =>
                    p === '...' ? (
                      <span key={`e-${idx}`} className="px-2 text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToPage(p)}
                        className={`min-w-[2.25rem] rounded-md px-3 py-1 text-sm ${
                          p === currentPage ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <button
              type="button"
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-label="close"
              onClick={closeModal}
            />
            <div
              className={`relative z-10 inline-block max-h-[90vh] w-full overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl sm:my-8 sm:max-w-3xl sm:align-middle ${isRTL ? 'text-right' : 'text-left'}`}
            >
              <form onSubmit={handleSubmit}>
                <div className="max-h-[85vh] overflow-y-auto px-4 pb-6 pt-5 sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingId != null ? t('challenges.modalTitleEdit') : t('challenges.modalTitle')}
                    </h3>
                    <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                      <XIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldName')} *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    <div className="quill-challenge-wrapper">
                      <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldDescription')} *</label>
                      <div className={`rounded-md border border-gray-300 bg-white ${isRTL ? 'ql-rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
                        <ReactQuill
                          key={editingId != null ? `edit-${editingId}` : 'add'}
                          theme="snow"
                          value={form.description}
                          onChange={(html) => setField('description', html)}
                          modules={quillModules}
                          formats={quillFormats}
                          className="challenge-quill"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldStatus')}</label>
                      <select
                        value={form.status}
                        onChange={(e) => setField('status', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="draft">{t('challenges.statusDraft')}</option>
                        <option value="active">{t('challenges.statusActive')}</option>
                        <option value="inactive">{t('challenges.statusInactive')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldCorrectAnswer')} *</label>
                      <input
                        type="text"
                        value={form.correctAnswer}
                        onChange={(e) => setField('correctAnswer', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldStartsAt')} *</label>
                        <input
                          type="datetime-local"
                          value={form.startsAt}
                          onChange={(e) => setField('startsAt', e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldEndsAt')} *</label>
                        <input
                          type="datetime-local"
                          value={form.endsAt}
                          onChange={(e) => setField('endsAt', e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">{t('challenges.fieldLearningLevel')}</label>
                      <select
                        value={form.learningLevelId}
                        onChange={(e) => setField('learningLevelId', e.target.value)}
                        disabled={loadingLearningLevels}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                      >
                        <option value="">{t('challenges.selectLearningLevel')}</option>
                        {learningLevels.map((level) => (
                          <option key={level.id} value={String(level.id)}>
                            {getLevelDisplayName(level)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="mb-1 text-base font-medium text-gray-900">{t('challenges.challengeScope')}</h4>
                      <p className="mb-4 text-sm text-gray-500">{t('challenges.scopeHint')}</p>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.country')}</label>
                          <select
                            value={form.scope.countryId}
                            onChange={(e) => handleScopeChange('countryId', e.target.value)}
                            disabled={loadingOrgData}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{loadingOrgData ? t('users.loadingCountries') : t('users.selectCountry')}</option>
                            {countries.map((country) => (
                              <option key={country.id} value={String(country.id)}>
                                {country.translatedName || country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.region')}</label>
                          <select
                            value={form.scope.regionId}
                            onChange={(e) => handleScopeChange('regionId', e.target.value)}
                            disabled={!form.scope.countryId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{!form.scope.countryId ? t('users.selectCountryFirst') : t('users.selectRegion')}</option>
                            {regions.map((region) => (
                              <option key={region.id} value={String(region.id)}>
                                {region.translatedName || region.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.academy')}</label>
                          <select
                            value={form.scope.academyId}
                            onChange={(e) => handleScopeChange('academyId', e.target.value)}
                            disabled={!form.scope.regionId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{!form.scope.regionId ? t('users.selectRegionFirst') : t('users.selectAcademy')}</option>
                            {academies.map((academy) => (
                              <option key={academy.id} value={String(academy.id)}>
                                {academy.translatedName || academy.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.district')}</label>
                          <select
                            value={form.scope.districtId}
                            onChange={(e) => handleScopeChange('districtId', e.target.value)}
                            disabled={!form.scope.academyId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{!form.scope.academyId ? t('users.selectAcademyFirst') : t('users.selectDistrict')}</option>
                            {districts.map((district) => (
                              <option key={district.id} value={String(district.id)}>
                                {district.translatedName || district.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.institution')}</label>
                          <select
                            value={form.scope.institutionId}
                            onChange={(e) => handleScopeChange('institutionId', e.target.value)}
                            disabled={!form.scope.districtId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{!form.scope.districtId ? t('users.selectDistrictFirst') : t('users.selectInstitution')}</option>
                            {institutions.map((institution) => (
                              <option key={institution.id} value={String(institution.id)}>
                                {institution.translatedName || institution.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">{t('users.department')}</label>
                          <select
                            value={form.scope.departmentId}
                            onChange={(e) => handleScopeChange('departmentId', e.target.value)}
                            disabled={!form.scope.institutionId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
                          >
                            <option value="">{!form.scope.institutionId ? t('users.selectInstitutionFirst') : t('users.selectDepartment')}</option>
                            {departments.map((department) => (
                              <option key={department.id} value={String(department.id)}>
                                {department.translatedName || department.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`mt-6 flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                    >
                      {isSaving
                        ? t('common.loading')
                        : editingId != null
                          ? t('challenges.submitUpdate')
                          : t('challenges.submitCreate')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .challenge-quill .ql-container { min-height: 200px; font-size: 14px; }
        .quill-challenge-wrapper .ql-toolbar { border-top-left-radius: 0.375rem; border-top-right-radius: 0.375rem; }
        .quill-challenge-wrapper .ql-container { border-bottom-left-radius: 0.375rem; border-bottom-right-radius: 0.375rem; }
        .challenge-quill .ql-editor img { max-width: 100%; height: auto; vertical-align: middle; }
      `}</style>
    </div>
  );
};

export default Challenges;
